import { useEffect, useRef, useState, type ReactNode } from "react";
import { DuoModeDefaultToggle } from "../components/settings/DuoModeDefaultToggle";
import { DefaultModeRow } from "../components/settings/DefaultModeRow";
import { SettingsGroup } from "../components/settings/SettingsGroup";
import { SettingsRow } from "../components/settings/SettingsRow";
import { SettingsSwitch } from "../components/settings/SettingsSwitch";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import {
  changeEmail,
  changePassword,
  reauthenticateWithEmail,
  renderGoogleLinkButton,
  signOut,
} from "../lib/auth-client";
import { authedFetch } from "../lib/api-client";
import { auth } from "../lib/firebase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { useT } from "../lib/use-t";

/**
 * Settings page — grouped Vertrouwd cards. Hosts the sticky preferences that
 * don't belong inside an exam session. Auth: enforced by the parent route's
 * `loader: requireSession`.
 *
 * Backed controls persist optimistically per-control (Standaardmodus, DUO
 * default, taal, naam/e-mail/wachtwoord) — there is no page-level save bar.
 * Rows with no backing field/endpoint are visibly inert "Binnenkort"
 * placeholders (see PlaceholderSwitch / the disabled select + date input).
 */
type Me = {
  firebaseUid: string;
  email: string;
  displayName: string;
  roles: ("student" | "admin")[];
  useDuoSkinDefault: boolean;
  defaultModule: string | null;
};

// 17px stroke icons (inline SVG — no emoji-as-icon per brand.md).
function Ic({ children }: { children: ReactNode }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
const IconAccount = (
  <Ic>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21a8 8 0 0116 0" />
  </Ic>
);
const IconLanguage = (
  <Ic>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
  </Ic>
);
const IconAudio = (
  <Ic>
    <path d="M11 5L6 9H2v6h4l5 4z" />
    <path d="M15.5 8.5a5 5 0 010 7M19 5a9 9 0 010 14" />
  </Ic>
);
const IconPractice = (
  <Ic>
    <path d="M12 4v16M4 8l8-4 8 4" />
    <path d="M5 10v8a2 2 0 002 2h10a2 2 0 002-2v-8" />
  </Ic>
);
const IconExam = (
  <Ic>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4" />
  </Ic>
);
const IconManage = (
  <Ic>
    <path d="M16 17l5-5-5-5M21 12H9M13 21H6a2 2 0 01-2-2V5a2 2 0 012-2h7" />
  </Ic>
);

export function SettingsPage() {
  const t = useT();
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch("/auth/me");
        if (!res.ok) return;
        const json = (await res.json()) as { ok: true; data: Me } | { ok: false };
        if (!cancelled && json.ok) setMe(json.data);
      } catch {
        // Why: controls stay at defaults until next /auth/me fetch succeeds.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const defaultModule =
    me?.defaultModule === "schrijven" || me?.defaultModule === "spreken"
      ? me.defaultModule
      : null;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-5 pb-24 sm:px-6 sm:py-8 sm:pb-28">
      <header className="space-y-1.5">
        <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("settings.groups.account")}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--ink)] sm:text-3xl">
          {t("settings.title")}
        </h1>
        <p className="text-sm text-[var(--ink-muted)]">{t("settings.subtitle")}</p>
      </header>

      {/* Account */}
      <SettingsGroup title={t("settings.groups.account")} icon={IconAccount}>
        <SettingsRow title={t("settings.nameRowTitle")} helper={t("settings.nameRowHelp")}>
          <ChangeDisplayNameRow current={me?.displayName ?? null} />
        </SettingsRow>
        <SettingsRow title={t("settings.emailRowTitle")} helper={t("settings.emailRowHelp")}>
          <ChangeEmailRow currentEmail={me?.email ?? null} />
        </SettingsRow>
        <SettingsRow title={t("settings.passwordRowTitle")} helper={t("settings.passwordRowHelp")}>
          <ChangePasswordRow />
        </SettingsRow>
        <SettingsRow title="Google" helper={t("settings.linkGoogle.linkAriaLabel")}>
          <LinkGoogleRow />
        </SettingsRow>
      </SettingsGroup>

      {/* Taal & weergave */}
      <SettingsGroup title={t("settings.groups.language")} icon={IconLanguage}>
        <SettingsRow
          title={t("settings.interfaceLanguage")}
          helper={t("settings.interfaceLanguageHelp")}
        >
          <LanguageSwitcher />
        </SettingsRow>
        <SettingsRow title={t("settings.largerText")} helper={t("settings.largerTextHelp")}>
          <PlaceholderSwitch label={t("settings.largerText")} />
        </SettingsRow>
      </SettingsGroup>

      {/* Audio */}
      <SettingsGroup title={t("settings.groups.audio")} icon={IconAudio}>
        <SettingsRow title={t("settings.ttsVoice")} helper={t("settings.ttsVoiceHelp")}>
          {/* Why: static placeholder — no TTS-voice preference column/endpoint. */}
          <select
            disabled
            aria-label={t("settings.ttsVoice")}
            title={t("settings.comingSoon")}
            className="min-h-11 w-full cursor-not-allowed rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--panel-soft)] px-3 py-2.5 text-sm text-[var(--ink-muted)] opacity-60 sm:w-auto sm:min-w-[200px]"
          >
            <option>{t("settings.comingSoon")}</option>
          </select>
        </SettingsRow>
        <SettingsRow title={t("settings.autoplay")} helper={t("settings.autoplayHelp")}>
          <PlaceholderSwitch label={t("settings.autoplay")} defaultChecked />
        </SettingsRow>
      </SettingsGroup>

      {/* Oefenen */}
      <SettingsGroup title={t("settings.groups.practice")} icon={IconPractice}>
        <SettingsRow
          title={t("settings.practice.defaultMode")}
          helper={t("settings.practice.defaultModeHelp")}
        >
          {me ? (
            <DefaultModeRow key={defaultModule ?? "none"} initialValue={defaultModule} />
          ) : (
            <DefaultModeRow initialValue={null} />
          )}
        </SettingsRow>
        <SettingsRow
          title={t("settings.duoModeHeading")}
          helper={t("settings.duoModeDescription")}
        >
          {me ? (
            <DuoModeDefaultToggle
              key={String(me.useDuoSkinDefault)}
              initialValue={me.useDuoSkinDefault}
            />
          ) : (
            <DuoModeDefaultToggle initialValue={false} />
          )}
        </SettingsRow>
        <SettingsRow
          title={t("settings.practice.dailyGoal")}
          helper={t("settings.practice.dailyGoalHelp")}
        >
          <DailyGoalPlaceholder ariaLabel={t("settings.practice.dailyGoal")} />
        </SettingsRow>
        <SettingsRow
          title={t("settings.practice.reminder")}
          helper={t("settings.practice.reminderHelp")}
        >
          <PlaceholderSwitch label={t("settings.practice.reminder")} />
        </SettingsRow>
      </SettingsGroup>

      {/* Examen */}
      <SettingsGroup title={t("settings.groups.exam")} icon={IconExam}>
        <SettingsRow
          title={t("settings.examDate")}
          helper={t("settings.examDateHelp")}
          htmlFor="settings-exam-date"
        >
          {/* Why: static placeholder — no examDate column/endpoint yet. */}
          <input
            id="settings-exam-date"
            type="date"
            disabled
            title={t("settings.comingSoon")}
            className="min-h-11 w-full cursor-not-allowed rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--panel-soft)] px-3 py-2.5 text-sm text-[var(--ink-muted)] opacity-60 sm:w-auto sm:min-w-[170px]"
          />
        </SettingsRow>
        <SettingsRow title={t("settings.showCountdown")} helper={t("settings.showCountdownHelp")}>
          <PlaceholderSwitch label={t("settings.showCountdown")} defaultChecked />
        </SettingsRow>
      </SettingsGroup>

      {/* Account beheren */}
      <SettingsGroup title={t("settings.groups.manage")} icon={IconManage} tone="warm">
        <SettingsRow title={t("settings.logout")} helper={t("settings.logoutHelp")}>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={async () => {
              await signOut();
              window.location.href = "/login";
            }}
          >
            {t("settings.logout")}
          </Button>
        </SettingsRow>
        <SettingsRow
          title={t("settings.deleteAccount")}
          helper={t("settings.deleteAccountHelp")}
        >
          {/* Why: no account-deletion endpoint exists — visibly inert, not wired. */}
          <Button
            type="button"
            variant="secondary"
            size="md"
            disabled
            title={t("settings.deleteAccountUnavailable")}
            className="border-[rgba(207,95,79,.4)] text-[var(--fail)]"
          >
            {t("settings.deleteAccount")}
          </Button>
        </SettingsRow>
      </SettingsGroup>
    </div>
  );
}

/** Inert toggle for not-yet-backed preferences. Local state only — never POSTs. */
function PlaceholderSwitch({
  label,
  defaultChecked = false,
}: {
  label: string;
  defaultChecked?: boolean;
}) {
  const t = useT();
  // Why: static placeholder — no backing field/endpoint yet. Disabled so it
  // can't masquerade as a persisted preference.
  return (
    <SettingsSwitch
      checked={defaultChecked}
      disabled
      label={label}
      title={t("settings.comingSoon")}
    />
  );
}

/** Inert 1/2/3 daily-goal segmented control. Local state only — never POSTs. */
function DailyGoalPlaceholder({ ariaLabel }: { ariaLabel: string }) {
  const t = useT();
  // Why: static placeholder — no daily-goal column/endpoint yet.
  return (
    <div className="flex flex-col items-end gap-1.5">
      <div
        role="group"
        aria-label={ariaLabel}
        title={t("settings.comingSoon")}
        className="inline-flex cursor-not-allowed items-center rounded-full border border-[var(--border)] bg-[var(--panel-soft)] p-1 opacity-60"
      >
        {["1", "2", "3"].map((n) => (
          <span
            key={n}
            className={[
              "inline-flex h-9 min-h-[36px] items-center rounded-full px-3 text-xs font-semibold sm:h-10 sm:text-sm",
              n === "2"
                ? "bg-[var(--panel)] text-[var(--accent)] shadow-[0_1px_2px_rgba(37,35,31,.08)]"
                : "text-[var(--ink-muted)]",
            ].join(" ")}
          >
            {n}
          </span>
        ))}
      </div>
    </div>
  );
}

function ChangeDisplayNameRow({ current }: { current: string | null }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(current ?? "");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (current) setName(current);
  }, [current]);

  return (
    <div className="w-full space-y-2 sm:w-auto sm:min-w-[240px]">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="text-sm text-[var(--ink)] hover:text-[var(--accent)]"
      >
        <span aria-hidden>{open ? "▾" : "▸"}</span> <span>{t("settings.changeName.toggle")}</span>
      </button>
      {open ? (
        <form
          id="form-display-name"
          aria-describedby={msg ? "form-display-name-msg" : undefined}
          onSubmit={async (e) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (trimmed.length < 1) {
              setMsg(t("settings.changeName.nameRequired"));
              return;
            }
            if (trimmed.length > 80) {
              setMsg(t("settings.changeName.nameTooLong"));
              return;
            }
            try {
              const res = await authedFetch("/user/preferences", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ displayName: trimmed }),
              });
              if (!res.ok) {
                console.error(`[settings] save displayName failed: ${res.status}`);
                setMsg(t("settings.changeName.saveFailed"));
                return;
              }
              setMsg(t("settings.changeName.saved"));
              window.location.reload();
            } catch (err) {
              setMsg(err instanceof Error ? err.message : t("chrome.common.unknownError"));
            }
          }}
          className="space-y-2"
        >
          <Input
            label={t("settings.changeName.nameLabel")}
            name="displayName"
            type="text"
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="primary" size="sm">
            {t("settings.changeName.submit")}
          </Button>
          {msg ? <p id="form-display-name-msg" className="text-xs text-[var(--ink-muted)]">{msg}</p> : null}
        </form>
      ) : null}
    </div>
  );
}

function ChangeEmailRow(_props: { currentEmail: string | null }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <div className="w-full space-y-2 sm:w-auto sm:min-w-[240px]">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="text-sm text-[var(--ink)] hover:text-[var(--accent)]"
      >
        <span aria-hidden>{open ? "▾" : "▸"}</span>{" "}
        <span>{t("settings.changeEmail.toggle")}</span>
      </button>
      {open ? (
        <form
          id="form-email"
          aria-describedby={msg ? "form-email-msg" : undefined}
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await changeEmail(email);
              setMsg(t("settings.changeEmail.confirmationSent"));
            } catch (err) {
              setMsg((err as Error).message);
            }
          }}
          className="space-y-2"
        >
          <Input label={t("settings.changeEmail.newEmailLabel")} name="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" variant="primary" size="sm">{t("settings.changeEmail.submit")}</Button>
          {msg ? <p id="form-email-msg" className="text-xs text-[var(--ink-muted)]">{msg}</p> : null}
        </form>
      ) : null}
    </div>
  );
}

function ChangePasswordRow() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [needsReauth, setNeedsReauth] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function attempt(currentPwArg: string | null) {
    setMsg(null);
    setSubmitting(true);
    try {
      if (currentPwArg) {
        await reauthenticateWithEmail(currentPwArg);
      }
      await changePassword(newPw);
      setMsg(t("settings.changePassword.passwordChanged"));
      setNewPw("");
      setCurrentPw("");
      setNeedsReauth(false);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "auth/requires-recent-login" && !currentPwArg) {
        setNeedsReauth(true);
        setMsg(t("settings.changePassword.requiresRecentLogin"));
      } else if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setMsg(t("settings.changePassword.wrongPassword"));
      } else if (code === "auth/weak-password") {
        setMsg(t("settings.changePassword.passwordTooShort"));
      } else {
        setMsg(err instanceof Error ? err.message : t("chrome.common.unknownError"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full space-y-2 sm:w-auto sm:min-w-[240px]">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="text-sm text-[var(--ink)] hover:text-[var(--accent)]"
      >
        <span aria-hidden>{open ? "▾" : "▸"}</span> <span>{t("settings.changePassword.toggle")}</span>
      </button>
      {open ? (
        <form
          id="form-password"
          aria-describedby={msg ? "form-password-msg" : undefined}
          onSubmit={async (e) => {
            e.preventDefault();
            if (newPw.length < 6) {
              setMsg(t("settings.changePassword.passwordTooShort"));
              return;
            }
            if (needsReauth && !currentPw) {
              setMsg(t("settings.changePassword.fillCurrentPassword"));
              return;
            }
            await attempt(needsReauth ? currentPw : null);
          }}
          className="space-y-2"
        >
          {needsReauth ? (
            <Input
              label={t("settings.changePassword.currentPasswordLabel")}
              name="currentPassword"
              type="password"
              required
              autoComplete="current-password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
            />
          ) : null}
          <Input
            label={t("settings.changePassword.newPasswordLabel")}
            name="newPassword"
            type="password"
            required
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          <Button type="submit" variant="primary" size="sm" disabled={submitting}>
            {needsReauth ? t("settings.changePassword.submitWithReauth") : t("settings.changePassword.submit")}
          </Button>
          {msg ? (
            <p id="form-password-msg" className="text-xs text-[var(--ink-muted)]">
              {msg}
            </p>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

function LinkGoogleRow() {
  const t = useT();
  const [msg, setMsg] = useState<string | null>(null);
  const [linked, setLinked] = useState<boolean>(() =>
    auth.currentUser?.providerData.some((p) => p.providerId === "google.com") ?? false,
  );
  const gsiContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("linked") === "google") {
      setLinked(true);
      setMsg(t("settings.linkGoogle.linked"));
      params.delete("linked");
      const next = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState(null, "", next);
    }
    const unsub = auth.onAuthStateChanged((u) => {
      setLinked(u?.providerData.some((p) => p.providerId === "google.com") ?? false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (linked) return;
    if (!gsiContainerRef.current) return;
    const container = gsiContainerRef.current;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        setTimeout(tryRender, 50);
        return;
      }
      renderGoogleLinkButton(container, {
        onSuccess: () => {
          setLinked(true);
          setMsg(t("settings.linkGoogle.linked"));
        },
        onError: (err) => {
          const code = (err as { code?: string }).code;
          if (code === "auth/credential-already-in-use") {
            setMsg(t("settings.linkGoogle.alreadyLinkedByOther"));
          } else {
            setMsg(err instanceof Error ? err.message : t("chrome.common.unknownError"));
          }
        },
      });
    };
    tryRender();
    return () => {
      cancelled = true;
    };
  }, [linked]);

  if (linked) {
    return (
      <div className="space-y-1">
        <p className="text-sm text-[var(--ink-muted)]">
          {t("settings.linkGoogle.linked")} <span className="text-[var(--pass)]">✓</span>
        </p>
        {msg ? <p className="text-xs text-[var(--ink-muted)]">{msg}</p> : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div ref={gsiContainerRef} aria-label={t("settings.linkGoogle.linkAriaLabel")} />
      {msg ? <p className="text-xs text-[var(--ink-muted)]">{msg}</p> : null}
    </div>
  );
}
